import { useState } from "react";
import { Circle, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { recordStopError, type RecordStopError } from "../../../lib/aiTakeover";
import { stopCallRecording, type RecordingStopReason } from "../../../lib/calls";
import { Note } from "../../sections/settings/panel/fields";
import { Btn } from "../../ui";

/**
 * "Stop recording" on a call an operator took over from the AI (ring R6c).
 *
 * The callee heard at the opening that the call is recorded and that they may
 * say so if they would rather it were not. After a takeover they say it to a
 * person, and this is what that person presses. They are talking on their own
 * mobile, so this card is the only place in the CRM the call has.
 *
 * The same request and the same rules as the call window's stop
 * (`RecordingStop.tsx`): who asked IS the confirmation, and nothing on screen
 * changes until the server has said yes, because the operator is about to
 * repeat it to somebody. The one difference is what the stop takes: here it
 * also erases what the AI recorded before the takeover, which is what the AI's
 * own refusal line promises (`takeover/stop-recording.ts` in the API), and the
 * menu says so before the press rather than after.
 *
 * A popover rather than the window's inline strip: the dock's foot is a narrow
 * row that already wraps, and the takeover button beside it opens the same way.
 * The line saying the recording stopped is the card's, read from the stream, so
 * every tab the operator has open says it; this keeps only what the answer
 * alone knows - a leftover at Twilio, or a failure.
 */
export function AiRecordingStop({
  callId,
  canStop,
  onStopped,
}: {
  callId: string;
  /** The server's `canStopRecording`: false once stopped, and for anybody but the taker. */
  canStop: boolean;
  onStopped: () => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<RecordStopError | null>(null);
  const [leftover, setLeftover] = useState(false);

  async function stop(reason: RecordingStopReason) {
    setBusy(true);
    setError(null);
    try {
      const answer = await stopCallRecording(callId, reason);
      // Stopped, and Twilio kept the part captured before it. Not a failure:
      // the recording is over and our side refuses to download that copy.
      setLeftover(!answer.destroyed);
      onStopped();
    } catch (err) {
      setError(recordStopError(err));
    } finally {
      setBusy(false);
      // Closed either way, so the answer below is not hidden under the menu.
      setOpen(false);
    }
  }

  return (
    <>
      {canStop ? (
        <Popover
          open={open}
          onOpenChange={(next) => {
            // Held open while the request is out: a stray click must not hide
            // the one control whose answer the operator is waiting for.
            if (!busy) setOpen(next);
          }}
        >
          <PopoverTrigger asChild>
            <Btn small disabled={busy}>
              {busy ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Circle className="size-2.5 fill-red-500 text-red-500" aria-hidden="true" />
              )}
              {t("aiCalls.live.recording.stop")}
            </Btn>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 p-2">
            <p className="mb-2 text-xs font-[600]">{t("aiCalls.live.recording.who")}</p>
            <div className="flex flex-col gap-1.5">
              <Btn small tone="dark" className="w-full justify-start" disabled={busy} onClick={() => void stop("objection")}>
                {t("aiCalls.live.recording.byCustomer")}
              </Btn>
              <Btn small className="w-full justify-start" disabled={busy} onClick={() => void stop("operator")}>
                {t("aiCalls.live.recording.byUs")}
              </Btn>
            </div>
            <p className="crm-sub mt-2 text-xs">{t("aiCalls.live.recording.erases")}</p>
          </PopoverContent>
        </Popover>
      ) : null}
      {leftover ? (
        <Note tone="warn" className="crm-ailive-error">
          {t("aiCalls.live.recording.leftover")}
        </Note>
      ) : null}
      {/* Loud, because a failed stop changes what the operator should SAY: they
          may have just told somebody the recording is off. */}
      {error ? (
        <Note tone="bad" live className="crm-ailive-error">
          {t(`aiCalls.live.recording.error.${error}`)}
        </Note>
      ) : null}
    </>
  );
}
